import LegalLayout, { LegalList, Section } from "@/components/LegalLayout";
import { PRIVACY_CONTACT_EMAIL } from "@/lib/legal";

export default function DataHandlingPage() {
  return (
    <LegalLayout eyebrow="Transparency" title="How Callsect handles a call" updated="23 August 2026">
      <Section title="Conversation intake">
        <p>
          The signed-in user can connect Google Meet for automatic transcript intake, import a full speaker-labelled transcript,
          or use manual audio upload as a fallback.
          Google access is read-only and its OAuth credentials are encrypted at rest. For manual upload, the user confirms authority
          and Callsect validates the relevant format and size. Audio duration is also verified. Every path enforces the applicable account quotas.
        </p>
      </Section>

      <Section title="During transcript import">
        <ol className="list-decimal space-y-3 pl-5 marker:text-indigo-400">
          <li>The user uploads TXT, MD, DOCX, VTT or a supported CSV export, or pastes speaker-labelled text.</li>
          <li>Callsect creates a non-billable preview and requires the user to confirm which detected speaker is YOU; all other detected speakers become CLIENT.</li>
          <li>The preview expires automatically after one hour if it is not confirmed and can be dismissed immediately.</li>
          <li>After confirmation, the saved transcript enters the normal analysis workflow without being sent to AssemblyAI.</li>
        </ol>
        <p>Summary-only notes and transcripts that do not contain at least two distinguishable speakers are rejected before analysis.</p>
      </Section>

      <Section title="During Google Meet import">
        <ol className="list-decimal space-y-3 pl-5 marker:text-indigo-400">
          <li>Callsect periodically checks the connected user&apos;s recent Meet conference records.</li>
          <li>For completed transcripts, it retrieves structured entries, speaker timing and participant display names through the Google Meet API.</li>
          <li>When one participant matches one existing Relationship exactly, the Callsect pipeline starts automatically.</li>
          <li>Ambiguous meetings wait in Meeting Inbox without affecting Conversations or Relationships until the user confirms the Customer.</li>
        </ol>
        <p>Callsect does not download the original Google Meet recording and does not request general access to Google Drive.</p>
      </Section>

      <Section title="During transcription">
        <ol className="list-decimal space-y-3 pl-5 marker:text-indigo-400">
          <li>The browser sends the recording over HTTPS to the Callsect backend hosted on Render.</li>
          <li>The backend writes it to temporary server storage for validation.</li>
          <li>The audio is uploaded to AssemblyAI through the configured EU API endpoint.</li>
          <li>AssemblyAI returns transcript text, detected language, speaker turns and timestamps.</li>
          <li>Callsect requests immediate deletion of the AssemblyAI transcript and its associated uploaded audio.</li>
          <li>The temporary Render-side file is deleted after the transcription attempt, including failure paths.</li>
        </ol>
        <p>
          If the AssemblyAI upload succeeds but no transcript identifier is created, or if the deletion request
          fails, immediate remote deletion cannot be confirmed and AssemblyAI&apos;s standard retention rules may apply.
        </p>
      </Section>

      <Section title="During analysis">
        <p>
          Callsect sends the transcript text and speaker-labelled turns to OpenAI to generate the requested
          dissection, CRM notes, coaching, next-call preparation and relationship-memory updates. The original audio is not sent to
          OpenAI in the current implementation.
        </p>
      </Section>

      <Section title="What Callsect keeps">
        <LegalList>
          <li>Call metadata, transcript, speaker segments, duration and limited transcription metadata.</li>
          <li>Linked sign-in provider and stable provider identifier; separately connected Meet identity, encrypted OAuth credentials, sync metadata and minimal source identifiers used to prevent duplicate imports.</li>
          <li>Dissection, CRM notes, coaching, next-call preparation and relationship history.</li>
          <li>Writing DNA data if the user chooses to create a profile.</li>
          <li>Account, legal-acceptance, quota, limited security records and first-party onboarding events needed to run and evaluate the private beta.</li>
        </LegalList>
        <p>The original audio recording is not saved in the Callsect database. Product events do not contain transcript text, call titles, customer names or raw audio.</p>
      </Section>

      <Section title="User control">
        <p>
          A user can unlink Google Sign-In after establishing a password, disconnect Google Meet, dismiss an Inbox transcript or temporary transcript preview, delete an individual call, export account data in JSON, or permanently delete the whole
          account and its user-scoped data, including first-party product events. Deleting a call also updates
          or removes the related relationship memory. For questions or rights requests, contact{" "}
          <a className="text-indigo-300 hover:text-indigo-200" href={`mailto:${PRIVACY_CONTACT_EMAIL}`}>{PRIVACY_CONTACT_EMAIL}</a>.
        </p>
      </Section>

      <Section title="Important limitations">
        <p>
          Callsect cannot determine whether the uploader has satisfied every recording-law, employment-law,
          confidentiality or customer-contract requirement. That responsibility remains with the uploader and
          their organisation. Provider logs, backups or exceptional failure paths may follow provider-configured
          retention periods as described in the Privacy Notice.
        </p>
      </Section>
    </LegalLayout>
  );
}
